import merge from 'lodash/merge';

/**
 * The Metrics class provides a way to manage metric objects across different contexts.
 * It allows retrieving metrics with context-specific values and properties.
 */
export class Metrics {
  /**
   * Creates a new Metrics instance
   * @param {string} context - The context to use for metrics (e.g., 'yourPosts', 'boardDetail')
   */
  constructor(context) {
    if (!context) {
      throw new Error('Context is required for Metrics');
    }
    this.context = context;
  }

  /**
   * Get a metric with context-specific properties applied
   * @param {Object} metricObj - The base metric object
   * @returns {Object} - Merged metric with context-specific values
   */
  get(metricObj) {
    return merge({}, metricObj, metricObj[this.context]);
  }

  /**
   * Get a list of metrics with context-specific properties applied
   * @param {Object} metricObjList - List of base metric objects
   * @returns [{Object}] - List of metrics with context-specific values
   */
  all(metricObjList) {
    return metricObjList.map((metricObj) => this.get(metricObj));
  }
}

/**
 * Get a metrics instance for a specific context
 * @param {string} context - The context to use for metrics
 * @returns {Metrics} - A metrics instance for the context
 */
export const getMetrics = (context) => new Metrics(context);

export const postTypes = Object.freeze({
  ORGANIC: 'organic',
  PROMOTED: 'promoted',
  ORGANIC_PROMOTED: 'organic-promoted',
  ADS: 'ads',
});

export const METRICS = Object.freeze({
  INSTAGRAM: {
    AVG_REACH: {
      label: 'Avg. Reach',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'instagramAvgReach',
        value: 'INSTAGRAM_AVG_REACH',
        info: {
          title: 'Average Reach',
          content: 'The average reach for your posts in this Board',
          footer: 'Reach = The number of unique users who saw this post',
        },
      },
      yourPosts: { attribute: 'reach', value: 'INSTAGRAM_REACH' },
    },
    ORGANIC_COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'comments_count', value: 'INSTAGRAM_ORGANIC_COMMENTS' },
      yourPosts: { attribute: 'commentsCount', value: 'INSTAGRAM_ORGANIC_COMMENTS' },
    },
    PAID_COMMENTS: {
      label: 'Comments',
      postType: postTypes.PROMOTED,
      boardDetail: { attribute: 'paid_comments', value: 'INSTAGRAM_PAID_COMMENTS' },
      yourPosts: { attribute: 'paidComments', value: 'INSTAGRAM_PAID_COMMENTS' },
    },
    TOTAL_COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'instagramTotalComments',
        value: 'INSTAGRAM_TOTAL_COMMENTS',
        postType: postTypes.ORGANIC,
        info: {
          title: 'Comments',
          content: 'The number of comments on posts in this Board',
        },
      },
      boardDetail: { attribute: 'total_comments', value: 'INSTAGRAM_TOTAL_COMMENTS' },
      yourPosts: { attribute: 'totalComments', value: 'INSTAGRAM_TOTAL_COMMENTS' },
    },
    AVG_EFFECTIVENESS: {
      label: 'Avg. Effectiveness',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'instagramAvgEffectiveness',
        value: 'INSTAGRAM_EFFECTIVENESS',
        info: 'The average effectiveness of posts in this Board\n\nEffectiveness = Engagements / Reach',
      },
    },
    EFFECTIVENESS: {
      label: 'Effectiveness - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardDetail: { attribute: 'effectiveness', value: 'INSTAGRAM_EFFECTIVENESS' },
      yourPosts: { attribute: 'effectiveness', value: 'INSTAGRAM_EFFECTIVENESS' },
    },
    AVG_EFFECTIVENESS_ENGAGEMENTS: {
      label: 'Avg. Effectiveness',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'instagramAvgEffectivenessEngagements',
        value: 'INSTAGRAM_EFFECTIVENESS_ENGAGEMENTS',
        info: {
          title: 'Average Effectiveness',
          content: 'The average effectiveness of posts in this Board',
          footer: 'Effectiveness = Engagements / Reach',
        },
      },
    },
    EFFECTIVENESS_ENGAGEMENTS: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardDetail: {
        attribute: 'effectiveness_engagements',
        value: 'INSTAGRAM_EFFECTIVENESS_ENGAGEMENTS',
      },
      yourPosts: {
        attribute: 'effectivenessEngagements',
        value: 'INSTAGRAM_EFFECTIVENESS_ENGAGEMENTS',
      },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate (Followers)',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'instagramAvgEngagement',
        value: 'INSTAGRAM_ENGAGEMENT',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\n' +
            'Engagement Rate = (Likes + Comments + Saves + Shares + Reposts) / Follower Snapshot\n\n' +
            'Follower Snapshot = Follower count on day of publishing',
        },
      },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate (Followers)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'engagement', value: 'INSTAGRAM_ENGAGEMENT_RATE' },
      yourPosts: { attribute: 'engagement', value: 'INSTAGRAM_ENGAGEMENT_RATE' },
    },
    ENGAGEMENT_RATE_IMPRESSIONS: {
      label: 'Engagement Rate (Impressions) - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'engagementRateImpressions',
        value: 'INSTAGRAM_ENGAGEMENT_RATE_IMPRESSIONS',
      },
    },
    ORGANIC_ENGAGEMENT_RATE_VIEWS: {
      label: 'Engagement Rate (Views)',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'engagement_rate_views',
        value: 'INSTAGRAM_ENGAGEMENT_RATE_VIEWS',
      },
      yourPosts: { attribute: 'engagementRateViews', value: 'INSTAGRAM_ENGAGEMENT_RATE_VIEWS' },
    },
    ORGANIC_IMPRESSIONS: {
      label: 'Impressions - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'impressions', value: 'INSTAGRAM_ORGANIC_IMPRESSIONS' },
      yourPosts: { attribute: 'impressions', value: 'INSTAGRAM_ORGANIC_IMPRESSIONS' },
    },
    PAID_IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.PROMOTED,
      boardDetail: { attribute: 'paid_impressions', value: 'INSTAGRAM_PAID_IMPRESSIONS' },
      yourPosts: { attribute: 'paidImpressions', value: 'INSTAGRAM_PAID_IMPRESSIONS' },
    },
    TOTAL_IMPRESSIONS: {
      label: 'Impressions - Total (Discontinued)',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'total_impressions', value: 'INSTAGRAM_TOTAL_IMPRESSIONS' },
      yourPosts: { attribute: 'totalImpressions', value: 'INSTAGRAM_TOTAL_IMPRESSIONS' },
    },
    ORGANIC_LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'like_count', value: 'INSTAGRAM_ORGANIC_LIKES' },
      yourPosts: { attribute: 'likeCount', value: 'INSTAGRAM_ORGANIC_LIKES' },
    },
    PAID_LIKES: {
      label: 'Likes',
      postType: postTypes.PROMOTED,
      boardDetail: { attribute: 'paid_likes', value: 'INSTAGRAM_PAID_LIKES' },
      yourPosts: { attribute: 'paidLikes', value: 'INSTAGRAM_PAID_LIKES' },
    },
    TOTAL_LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'instagramTotalLikes',
        value: 'INSTAGRAM_TOTAL_LIKES',
        postType: postTypes.ORGANIC,
        info: {
          title: 'Likes',
          content: 'The number of likes on posts in this Board',
        },
      },
      boardDetail: { attribute: 'total_likes', value: 'INSTAGRAM_TOTAL_LIKES' },
      yourPosts: { attribute: 'totalLikes', value: 'INSTAGRAM_TOTAL_LIKES' },
    },
    PAID_AND_ORGANIC_REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: { attribute: 'paidAndOrganicReach', value: 'INSTAGRAM_PAID_AND_ORGANIC_REACH' },
    },
    PAID_AND_ORGANIC_SAVED: {
      label: 'Saves',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: { attribute: 'paidAndOrganicSaved', value: 'INSTAGRAM_PAID_AND_ORGANIC_SAVED' },
    },
    PAID_AND_ORGANIC_SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: { attribute: 'paidAndOrganicShares', value: 'INSTAGRAM_PAID_AND_ORGANIC_SHARES' },
    },
    LIKESHOP_CLICKS: {
      label: 'LikeShop Clicks',
      postType: null,
      boardDetail: { attribute: 'likeshop_clicks', value: 'LIKESHOP_CLICKS' },
      yourPosts: { attribute: 'likeshopClicks', value: 'LIKESHOP_CLICKS' },
    },
    REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'reach', value: 'INSTAGRAM_REACH' },
      yourPosts: { attribute: 'reach', value: 'INSTAGRAM_REACH' },
    },
    PAID_REACH: {
      label: 'Reach',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidReach', value: 'INSTAGRAM_PAID_REACH' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'shares', value: 'INSTAGRAM_SHARES' },
      yourPosts: { attribute: 'shares', value: 'INSTAGRAM_SHARES' },
    },
    SAVES: {
      label: 'Saves',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'saved', value: 'INSTAGRAM_SAVES' },
      yourPosts: { attribute: 'saved', value: 'INSTAGRAM_SAVES' },
    },
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_engagement', value: 'INSTAGRAM_TOTAL_ENGAGEMENTS' },
      yourPosts: { attribute: 'totalEngagement', value: 'INSTAGRAM_TOTAL_ENGAGEMENTS' },
    },
    PAID_TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.PROMOTED,
      boardDetail: {
        attribute: 'paid_total_engagements',
        value: 'INSTAGRAM_PAID_TOTAL_ENGAGEMENTS',
      },
      yourPosts: { attribute: 'paidTotalEngagements', value: 'INSTAGRAM_PAID_TOTAL_ENGAGEMENTS' },
    },
    SUM_TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'sum_total_engagement', value: 'INSTAGRAM_SUM_TOTAL_ENGAGEMENTS' },
      yourPosts: { attribute: 'sumTotalEngagements', value: 'INSTAGRAM_SUM_TOTAL_ENGAGEMENTS' },
    },
    VIDEO_VIEWS: {
      label: 'Video Views - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'video_views', value: 'INSTAGRAM_VIDEO_VIEWS' },
      yourPosts: { attribute: 'videoViews', value: 'INSTAGRAM_VIDEO_VIEWS' },
    },
    PAID_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.PROMOTED,
      boardDetail: { attribute: 'paid_video_views', value: 'INSTAGRAM_PAID_VIDEO_VIEWS' },
      yourPosts: { attribute: 'paidVideoViews', value: 'INSTAGRAM_PAID_VIDEO_VIEWS' },
    },
    TOTAL_VIDEO_VIEWS: {
      label: 'Video Views - Total (Discontinued)',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'total_video_views', value: 'INSTAGRAM_TOTAL_VIDEO_VIEWS' },
      yourPosts: { attribute: 'totalVideoViews', value: 'INSTAGRAM_TOTAL_VIDEO_VIEWS' },
    },
    TOTAL_PLAYS: {
      label: 'All Plays - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'totalPlays', value: 'INSTAGRAM_TOTAL_PLAYS' },
    },
    VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'views', value: 'INSTAGRAM_VIEWS' },
      yourPosts: { attribute: 'views', value: 'INSTAGRAM_VIEWS' },
    },
    AVG_VIEW_DURATION: {
      label: 'Avg. Watch Time',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'avgTimeViewedSec', value: 'INSTAGRAM_AVG_WATCH_TIME' },
    },
    CPC: {
      label: 'CPC',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'cpc', value: 'INSTAGRAM_CPC' },
    },
    CPM: {
      label: 'CPM',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'cpm', value: 'INSTAGRAM_CPM' },
    },
    CTR: {
      label: 'CTR',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'ctr', value: 'INSTAGRAM_CTR' },
    },
    PAID_ENGAGEMENT_RATE: {
      label: 'Engagement Rate (Impressions)',
      postType: postTypes.PROMOTED,
      boardDetail: {
        attribute: 'paid_total_engagement_rate',
        value: 'INSTAGRAM_PAID_ENGAGEMENT_RATE',
      },
      yourPosts: { attribute: 'paidEngagementRate', value: 'INSTAGRAM_PAID_ENGAGEMENT_RATE' },
    },
    PAID_SAVES: {
      label: 'Saves',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidSaved', value: 'INSTAGRAM_PAID_SAVED' },
    },
    PAID_SHARES: {
      label: 'Shares',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidShares', value: 'INSTAGRAM_PAID_SHARES' },
    },
    ENTERTAINMENT_SCORE: {
      label: 'Entertainment Score',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'entertainmentScore', value: 'INSTAGRAM_ENTERTAINMENT_SCORE' },
    },
    AMOUNT_SPENT: {
      label: 'Amount Spent',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'amountSpent', value: 'INSTAGRAM_AMOUNT_SPENT' },
    },
    THRUPLAYS: {
      label: 'ThruPlays',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'thruplays', value: 'INSTAGRAM_THRUPLAYS' },
    },
    VIDEO_PLAYS_25: {
      label: 'Video Plays 25%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays25', value: 'INSTAGRAM_VIDEO_PLAYS_25' },
    },
    VIDEO_PLAYS_50: {
      label: 'Video Plays 50%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays50', value: 'INSTAGRAM_VIDEO_PLAYS_50' },
    },
    VIDEO_PLAYS_75: {
      label: 'Video Plays 75%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays75', value: 'INSTAGRAM_VIDEO_PLAYS_75' },
    },
    VIDEO_PLAYS_100: {
      label: 'Video Plays 100%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays100', value: 'INSTAGRAM_VIDEO_PLAYS_100' },
    },
    FOLLOWS: {
      label: 'Follows',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'follows', value: 'INSTAGRAM_FOLLOWS' },
    },
    PROFILE_VISITS: {
      label: 'Profile Visits',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'profileVisits', value: 'INSTAGRAM_PROFILE_VISITS' },
    },
    TOTAL_WATCH_TIME: {
      label: 'Total Watch Time',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_time_viewed_sec', value: 'INSTAGRAM_TOTAL_WATCH_TIME' },
      yourPosts: { attribute: 'totalTimeViewedSecLong', value: 'INSTAGRAM_TOTAL_WATCH_TIME' },
    },
    REPOSTS: {
      label: 'Reposts',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'reposts', value: 'INSTAGRAM_REPOSTS' },
      yourPosts: { attribute: 'reposts', value: 'INSTAGRAM_REPOSTS' },
    },
    REELS_SKIP_RATE: {
      label: 'Skip Rate',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'reels_skip_rate', value: 'INSTAGRAM_REELS_SKIP_RATE' },
      yourPosts: { attribute: 'reelsSkipRate', value: 'INSTAGRAM_REELS_SKIP_RATE' },
    },
    CROSSPOSTED_VIEWS: {
      label: 'Views - Crossposted',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'crossposted_views', value: 'INSTAGRAM_CROSSPOSTED_VIEWS' },
      yourPosts: { attribute: 'crosspostedViews', value: 'INSTAGRAM_CROSSPOSTED_VIEWS' },
    },
    FACEBOOK_VIEWS: {
      label: 'Views - Facebook',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'facebook_views', value: 'INSTAGRAM_FACEBOOK_VIEWS' },
      yourPosts: { attribute: 'facebookViews', value: 'INSTAGRAM_FACEBOOK_VIEWS' },
    },
  },
  INSTAGRAM_STORY: {
    REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'reach', value: 'INSTAGRAM_STORY_FRAME_REACH' },
      yourPosts: { attribute: 'reach', value: 'INSTAGRAM_STORY_FRAME_REACH' },
    },
    IMPRESSIONS: {
      label: 'Impressions - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'views', value: 'INSTAGRAM_STORY_FRAME_IMPRESSIONS' },
      yourPosts: { attribute: 'views', value: 'INSTAGRAM_STORY_FRAME_IMPRESSIONS' },
    },
    VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'views_v2', value: 'INSTAGRAM_STORY_FRAME_VIEWS_V2' },
      yourPosts: { attribute: 'viewsV2', value: 'INSTAGRAM_STORY_FRAME_VIEWS_V2' },
    },
    COMPLETION_RATE: {
      label: 'Completion Rate - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'completion_rate', value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE' },
      yourPosts: { attribute: 'completionRate', value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE' },
    },
    COMPLETION_RATE_VIEWS: {
      label: 'Completion Rate',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'completion_rate_views',
        value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE_VIEWS',
      },
      yourPosts: {
        attribute: 'completionRateViews',
        value: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE_VIEWS',
      },
    },
    EXIT_RATE: {
      label: 'Exit Rate - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'exit_rate', value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE' },
      yourPosts: { attribute: 'exitRate', value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE' },
    },
    EXIT_RATE_VIEWS: {
      label: 'Exit Rate',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'exit_rate_views', value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE_VIEWS' },
      yourPosts: { attribute: 'exitRateViews', value: 'INSTAGRAM_STORY_FRAME_EXIT_RATE_VIEWS' },
    },
    EXITS: {
      label: 'Exits',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'exits', value: 'INSTAGRAM_STORY_FRAME_EXITS' },
      yourPosts: { attribute: 'exits', value: 'INSTAGRAM_STORY_FRAME_EXITS' },
    },
    LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'swipe_ups', value: 'INSTAGRAM_STORY_FRAME_SWIPE_UPS' },
      yourPosts: { attribute: 'swipeUps', value: 'INSTAGRAM_STORY_FRAME_SWIPE_UPS' },
    },
    REPLIES: {
      label: 'Replies',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'replies', value: 'INSTAGRAM_STORY_FRAME_REPLIES' },
      yourPosts: { attribute: 'replies', value: 'INSTAGRAM_STORY_FRAME_REPLIES' },
    },
    TAPS_BACK: {
      label: 'Taps Back',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'taps_back', value: 'INSTAGRAM_STORY_FRAME_TAPS_BACK' },
    },
    TAPS_FORWARD: {
      label: 'Taps Forward',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'taps_forward', value: 'INSTAGRAM_STORY_FRAME_TAPS_FORWARD' },
    },
    AVG_REACH: {
      label: 'Avg. Reach',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'igStoryAvgReach',
        value: 'IG_STORY_AVG_REACH',
        info: {
          title: 'Average Reach',
          content: 'The average number of unique accounts who saw your story frames',
        },
      },
    },
    AVG_IMPRESSIONS: {
      label: 'Avg. Impressions - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'igStoryAvgImpressions',
        value: 'IG_STORY_AVG_IMPRESSIONS',
        info: 'The number of times your story frames were displayed',
      },
    },
    AVG_VIEWS: {
      label: 'Avg. Views',
      postType: postTypes.ORGANIC,
      formatType: 'number',
      boardsIndex: {
        attribute: 'igStoryAvgViews',
        value: 'IG_STORY_AVG_VIEWS',
        info: {
          title: 'Average Views',
          content: 'The number of times your story frames were viewed',
        },
      },
    },
    AVG_COMPLETION_RATE: {
      label: 'Avg. Completion Rate - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'igStoryAvgCompletionRate',
        value: 'IG_STORY_AVG_COMPLETION_RATE',
        info: 'The average completion rate of the story frames in this Board\n\nCompletion Rate = (Impression - Exits) / Impressions',
      },
    },
    AVG_COMPLETION_RATE_VIEWS: {
      label: 'Avg. Completion Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'igStoryAvgCompletionRateViews',
        value: 'IG_STORY_AVG_COMPLETION_RATE_VIEWS',
        info: {
          title: 'Average Completion Rate',
          content: 'The average completion rate of the story frames in this Board',
          footer: 'Completion Rate = (Views - Exits) / Views',
        },
      },
    },
    AVG_EXIT_RATE: {
      label: 'Avg. Exit Rate - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'igStoryAvgExitRate',
        value: 'IG_STORY_AVG_EXIT_RATE',
        info: 'The average exit rate of the story frames in this Board\n\nExit Rate = Exits / Impressions',
      },
    },
    AVG_EXIT_RATE_VIEWS: {
      label: 'Avg. Exit Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'igStoryAvgExitRateViews',
        value: 'IG_STORY_AVG_EXIT_RATE_VIEWS',
        info: {
          title: 'Average Exit Rate',
          content: 'The average exit rate of the story frames in this Board',
          footer: 'Exit Rate = Exits / Views',
        },
      },
    },
    SWIPE_UPS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'igStorySwipeUps',
        value: 'IG_STORY_SWIPE_UPS',
        info: {
          title: 'Link Clicks',
          content: 'The number of link clicks on story frames in this Board',
        },
      },
    },
  },
  TIKTOK: {
    TOTAL_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'tiktokTotalVideoViews',
        value: 'TIKTOK_TOTAL_VIDEO_VIEWS',
        info: {
          title: 'Video Views',
          content: 'The number of times your posts in this Board were viewed',
        },
      },
    },
    AVG_VIDEO_VIEWS: {
      label: 'Avg. Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'tiktokAvgVideoViews',
        value: 'TIKTOK_AVG_VIDEO_VIEWS',
        info: {
          title: 'Average Video Views',
          content: 'The average number of video views for posts in this Board',
          footer: 'Avg. Video Views = Video Views / Number of Media',
        },
      },
    },
    AVG_RETENTION_RATE: {
      label: 'Avg. Retention Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'tiktokAvgRetentionRate',
        value: 'TIKTOK_AVG_RETENTION_RATE',
        info: {
          title: 'Average Retention Rate',
          content: 'The average percentage of your videos in this Board that your audience watched',
          footer: 'Retention Rate = Average Time Watched / Video Duration',
        },
      },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'tiktokAvgEngagementRate',
        value: 'TIKTOK_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Likes + Comments + Shares) / Views',
        },
      },
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'views', value: 'TIKTOK_VIEWS' },
      yourPosts: { attribute: 'views', value: 'TIKTOK_VIEWS' },
    },
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'total_engagements', value: 'TIKTOK_TOTAL_ENGAGEMENTS' },
      yourPosts: { attribute: 'totalEngagements', value: 'TIKTOK_TOTAL_ENGAGEMENTS' },
    },
    EFFECTIVENESS: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'effectiveness', value: 'TIKTOK_EFFECTIVENESS' },
      yourPosts: { attribute: 'effectiveness', value: 'TIKTOK_EFFECTIVENESS' },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'engagement_rate', value: 'TIKTOK_ENGAGEMENT_RATE' },
      yourPosts: { attribute: 'engagementRate', value: 'TIKTOK_ENGAGEMENT_RATE' },
    },
    RETENTION_RATE: {
      label: 'Retention Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'average_completion_rate',
        value: 'TIKTOK_AVERAGE_COMPLETION_RATE',
      },
      yourPosts: { attribute: 'averageCompletionRate', value: 'TIKTOK_AVERAGE_COMPLETION_RATE' },
    },
    FYP_VIEWS: {
      label: 'FYP Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'impressions_for_you_rate',
        value: 'TIKTOK_IMPRESSIONS_FOR_YOU_RATE',
      },
      yourPosts: {
        attribute: 'impressionsForYouRate',
        value: 'TIKTOK_IMPRESSIONS_FOR_YOU_RATE',
      },
    },
    IMPRESSIONS_PERSONAL_PROFILE_RATE: {
      label: 'Profile Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'impressions_personal_profile_rate',
        value: 'TIKTOK_IMPRESSIONS_PERSONAL_PROFILE_RATE',
      },
      yourPosts: {
        attribute: 'impressionsPersonalProfileRate',
        value: 'TIKTOK_IMPRESSIONS_PERSONAL_PROFILE_RATE',
      },
    },
    AVERAGE_TIME_WATCHED: {
      label: 'Avg. Time Watched',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'average_time_watched', value: 'TIKTOK_AVERAGE_TIME_WATCHED' },
      yourPosts: { attribute: 'averageTimeWatched', value: 'TIKTOK_AVERAGE_TIME_WATCHED' },
    },
    REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'reach', value: 'TIKTOK_REACH' },
      yourPosts: { attribute: 'reach', value: 'TIKTOK_REACH' },
    },
    COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'comments', value: 'TIKTOK_COMMENTS' },
      yourPosts: { attribute: 'comments', value: 'TIKTOK_COMMENTS' },
    },
    FULL_VIDEO_WATCHED_RATE: {
      label: 'Completion Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'full_video_watched_rate',
        value: 'TIKTOK_FULL_VIDEO_WATCHED_RATE',
      },
      yourPosts: { attribute: 'fullVideoWatchedRate', value: 'TIKTOK_FULL_VIDEO_WATCHED_RATE' },
    },
    IMPRESSIONS_SEARCH_RATE: {
      label: 'Search Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'impressions_search_rate',
        value: 'TIKTOK_IMPRESSIONS_SEARCH_RATE',
      },
      yourPosts: { attribute: 'impressionsSearchRate', value: 'TIKTOK_IMPRESSIONS_SEARCH_RATE' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'shares', value: 'TIKTOK_SHARES' },
      yourPosts: { attribute: 'shares', value: 'TIKTOK_SHARES' },
    },
    IMPRESSIONS_SOUND_RATE: {
      label: 'Sound Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'impressions_sound_rate', value: 'TIKTOK_IMPRESSIONS_SOUND_RATE' },
      yourPosts: { attribute: 'impressionsSoundRate', value: 'TIKTOK_IMPRESSIONS_SOUND_RATE' },
    },
    IMPRESSIONS_FOLLOW_RATE: {
      label: 'Following Page Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'impressions_follow_rate',
        value: 'TIKTOK_IMPRESSIONS_FOLLOW_RATE',
      },
      yourPosts: { attribute: 'impressionsFollowRate', value: 'TIKTOK_IMPRESSIONS_FOLLOW_RATE' },
    },
    TOTAL_TIME_WATCHED: {
      label: 'Total Time Watched',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'total_time_watched', value: 'TIKTOK_TOTAL_TIME_WATCHED' },
      yourPosts: { attribute: 'totalTimeWatched', value: 'TIKTOK_TOTAL_TIME_WATCHED' },
    },
    IMPRESSIONS_HASHTAG_RATE: {
      label: 'Hashtag Views %',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'impressions_hashtag_rate',
        value: 'TIKTOK_IMPRESSIONS_HASHTAG_RATE',
      },
      yourPosts: { attribute: 'impressionsHashtagRate', value: 'TIKTOK_IMPRESSIONS_HASHTAG_RATE' },
    },
    DURATION: {
      label: 'Video Duration',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'duration', value: 'TIKTOK_DURATION' },
      yourPosts: { attribute: 'duration', value: 'TIKTOK_DURATION' },
    },
    LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'likes', value: 'TIKTOK_LIKES' },
      yourPosts: { attribute: 'likes', value: 'TIKTOK_LIKES' },
    },
    ENTERTAINMENT_SCORE: {
      label: 'Entertainment Score',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'entertainment_score', value: 'TIKTOK_ENTERTAINMENT_SCORE' },
      yourPosts: { attribute: 'entertainmentScore', value: 'TIKTOK_ENTERTAINMENT_SCORE' },
    },
    VIEWS_BASED_ENGAGEMENT_RATE: {
      label: 'Engagement Rate (Views)',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'viewsBasedEngagementRate',
        value: 'TIKTOK_VIEWS_BASED_ENGAGEMENT_RATE',
      },
    },
    ORGANIC_LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicLikes', value: 'TIKTOK_ORGANIC_LIKES' },
    },
    ORGANIC_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicEngagements', value: 'TIKTOK_ORGANIC_ENGAGEMENTS' },
    },
    ORGANIC_SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicShares', value: 'TIKTOK_ORGANIC_SHARES' },
    },
    ORGANIC_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicViews', value: 'TIKTOK_ORGANIC_VIEWS' },
    },
    ORGANIC_REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicReach', value: 'TIKTOK_ORGANIC_REACH' },
    },
    PAID_LIKES: {
      label: 'Likes',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidLikes', value: 'TIKTOK_PAID_LIKES' },
    },
    PAID_SHARES: {
      label: 'Shares',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidShares', value: 'TIKTOK_PAID_SHARES' },
    },
    PAID_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidEngagements', value: 'TIKTOK_PAID_ENGAGEMENTS' },
    },
    PAID_VIEWS: {
      label: 'Video Views',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidViews', value: 'TIKTOK_PAID_VIEWS' },
    },
    PAID_REACH: {
      label: 'Reach',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidReach', value: 'TIKTOK_PAID_REACH' },
    },
    PAID_ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidEngagementRate', value: 'TIKTOK_PAID_ENGAGEMENT_RATE' },
    },
    PAID_VIEWS_BASED_ENGAGEMENT_RATE: {
      label: 'Engagement Rate (Views)',
      postType: postTypes.PROMOTED,
      yourPosts: {
        attribute: 'paidViewsBasedEngagementRate',
        value: 'TIKTOK_PAID_VIEWS_BASED_ENGAGEMENT_RATE',
      },
    },
    FAVORITES: {
      label: 'Favorites',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'favorites', value: 'TIKTOK_FAVORITES' },
      yourPosts: { attribute: 'favorites', value: 'TIKTOK_FAVORITES' },
    },
    NEW_FOLLOWERS: {
      label: 'New Followers',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'new_followers', value: 'TIKTOK_NEW_FOLLOWERS' },
      yourPosts: { attribute: 'newFollowers', value: 'TIKTOK_NEW_FOLLOWERS' },
    },
    PROFILE_VIEWS: {
      label: 'Profile Views',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'profile_views', value: 'TIKTOK_PROFILE_VIEWS' },
      yourPosts: { attribute: 'profileViews', value: 'TIKTOK_PROFILE_VIEWS' },
    },
    ORGANIC_NEW_FOLLOWERS: {
      label: 'New Followers',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicNewFollowers', value: 'TIKTOK_ORGANIC_NEW_FOLLOWERS' },
    },
    ORGANIC_PROFILE_VIEWS: {
      label: 'Profile Views',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'organicProfileViews', value: 'TIKTOK_ORGANIC_PROFILE_VIEWS' },
    },
    PAID_NEW_FOLLOWERS: {
      label: 'New Followers',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidNewFollowers', value: 'TIKTOK_PAID_NEW_FOLLOWERS' },
    },
    PAID_PROFILE_VIEWS: {
      label: 'Profile Views',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidProfileViews', value: 'TIKTOK_PAID_PROFILE_VIEWS' },
    },
  },
  FACEBOOK: {
    // Incorrect: PostType should be ORGANIC_PROMOTED
    // use PAID_AND_ORGANIC_POST_CLICKS
    // can remove depending on data avalible on new naming.
    POST_CLICKS: {
      label: 'Post Clicks',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_POST_CLICKS',
      boardDetail: { attribute: 'post_clicks' },
      yourPosts: { attribute: 'postClicks' },
    },
    PAID_AND_ORGANIC_POST_CLICKS: {
      label: 'Post Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicPostClicks',
      value: 'FACEBOOK_PAID_AND_ORGANIC_POST_CLICKS',
    },
    COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC,
      attribute: 'comments',
      value: 'FACEBOOK_COMMENTS',
      yourPosts: { attribute: 'organicComments', value: 'FACEBOOK_COMMENTS' },
    },
    ORGANIC_COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC,
      attribute: 'organicComments',
      value: 'FACEBOOK_ORGANIC_COMMENTS',
    },
    PAID_COMMENTS: {
      label: 'Comments',
      postType: postTypes.PROMOTED,
      attribute: 'paidComments',
      value: 'FACEBOOK_PAID_COMMENTS',
    },
    PAID_AND_ORGANIC_COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicComments',
      value: 'FACEBOOK_PAID_AND_ORGANIC_COMMENTS',
    },
    REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC,
      attribute: 'reach',
      value: 'FACEBOOK_REACH',
    },
    ORGANIC_REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC,
      attribute: 'organicReach',
      value: 'FACEBOOK_ORGANIC_REACH',
    },
    // Deprecated
    PAID_REACH: {
      label: 'Reach',
      postType: postTypes.PROMOTED,
      attribute: 'paidReach',
      value: 'FACEBOOK_PAID_REACH',
    },
    PAID_REACH_V2: {
      label: 'Reach',
      postType: postTypes.PROMOTED,
      attribute: 'paidReachV2',
      value: 'FACEBOOK_PAID_REACH_V2',
    },
    // Deprecated
    PAID_AND_ORGANIC_REACH: {
      label: 'Reach',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicReach',
      value: 'FACEBOOK_PAID_AND_ORGANIC_REACH',
    },
    PAID_AND_ORGANIC_REACH_V2: {
      label: 'Reach',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicReachV2',
      value: 'FACEBOOK_PAID_AND_ORGANIC_REACH_V2',
    },
    // Deprecated
    EFFECTIVENESS: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC,
      attribute: 'effectiveness',
      value: 'FACEBOOK_EFFECTIVENESS',
    },
    // Deprecated
    ORGANIC_EFFECTIVENESS: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC,
      attribute: 'organicEffectiveness',
      value: 'FACEBOOK_ORGANIC_EFFECTIVENESS',
    },
    ORGANIC_EFFECTIVENESS_V2: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC,
      attribute: 'organicEffectivenessV2',
      value: 'FACEBOOK_ORGANIC_EFFECTIVENESS_V2',
    },
    PAID_EFFECTIVENESS_V2: {
      label: 'Effectiveness',
      postType: postTypes.PROMOTED,
      attribute: 'paidEffectivenessV2',
      value: 'FACEBOOK_PAID_EFFECTIVENESS_V2',
    },
    PAID_AND_ORGANIC_EFFECTIVENESS_V2: {
      label: 'Effectiveness',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicEffectivenessV2',
      value: 'FACEBOOK_PAID_AND_ORGANIC_EFFECTIVENESS_V2',
    },
    REACTIONS: {
      label: 'Reactions',
      postType: postTypes.ORGANIC,
      attribute: 'reactions',
      value: 'FACEBOOK_REACTIONS',
    },
    ORGANIC_REACTIONS: {
      label: 'Reactions',
      postType: postTypes.ORGANIC,
      attribute: 'organicReactions',
      value: 'FACEBOOK_ORGANIC_REACTIONS',
    },
    PAID_REACTIONS: {
      label: 'Reactions',
      postType: postTypes.PROMOTED,
      attribute: 'paidReactions',
      value: 'FACEBOOK_PAID_REACTIONS',
    },
    PAID_AND_ORGANIC_REACTIONS: {
      label: 'Reactions',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicReactions',
      value: 'FACEBOOK_PAID_AND_ORGANIC_REACTIONS',
    },
    // Deprecated
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_ENGAGEMENT_RATE',
      boardDetail: { attribute: 'engagement_rate' },
      yourPosts: { attribute: 'engagementRate' },
    },
    // Deprecated
    ORGANIC_ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      attribute: 'organicEngagementRate',
      value: 'FACEBOOK_ORGANIC_ENGAGEMENT_RATE',
    },
    ORGANIC_ENGAGEMENT_RATE_V2: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      attribute: 'organicEngagementRateV2',
      value: 'FACEBOOK_ORGANIC_ENGAGEMENT_RATE_V2',
    },
    // Deprecated
    PAID_ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.PROMOTED,
      attribute: 'paidEngagementRate',
      value: 'FACEBOOK_PAID_ENGAGEMENT_RATE',
    },
    PAID_ENGAGEMENT_RATE_V2: {
      label: 'Engagement Rate',
      postType: postTypes.PROMOTED,
      attribute: 'paidEngagementRateV2',
      value: 'FACEBOOK_PAID_ENGAGEMENT_RATE_V2',
    },
    PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicEngagementRateV2',
      value: 'FACEBOOK_PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2',
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      attribute: 'shares',
      value: 'FACEBOOK_SHARES',
    },
    ORGANIC_SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      attribute: 'organicShares',
      value: 'FACEBOOK_ORGANIC_SHARES',
    },
    PAID_SHARES: {
      label: 'Shares',
      postType: postTypes.PROMOTED,
      attribute: 'paidShares',
      value: 'FACEBOOK_PAID_SHARES',
    },
    PAID_AND_ORGANIC_SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicShares',
      value: 'FACEBOOK_PAID_AND_ORGANIC_SHARES',
    },
    // Depreacted
    IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      attribute: 'impressions',
      value: 'FACEBOOK_IMPRESSIONS',
    },
    // Deprecated with Deprecated Label
    ORGANIC_IMPRESSIONS_DEPRECATED: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      attribute: 'organicImpressions',
      value: 'FACEBOOK_ORGANIC_IMPRESSIONS',
    },
    // Deprecated with Deprecated Label
    PAID_IMPRESSIONS_DEPRECATED: {
      label: 'Impressions',
      postType: postTypes.PROMOTED,
      attribute: 'paidImpressions',
      value: 'FACEBOOK_PAID_IMPRESSIONS',
    },
    // Deprecated with Deprecated Label
    PAID_AND_ORGANIC_IMPRESSIONS_DEPRECATED: {
      label: 'Impressions',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicImpressions',
      value: 'FACEBOOK_PAID_AND_ORGANIC_IMPRESSIONS',
    },
    // Deprecated
    ORGANIC_IMPRESSIONS: {
      label: 'Impressions - Organic (Discontinued)',
      postType: postTypes.ORGANIC,
      attribute: 'organicImpressions',
      value: 'FACEBOOK_ORGANIC_IMPRESSIONS',
    },
    // Deprecated
    PAID_IMPRESSIONS: {
      label: 'Impressions - Promoted (Discontinued)',
      postType: postTypes.PROMOTED,
      attribute: 'paidImpressions',
      value: 'FACEBOOK_PAID_IMPRESSIONS',
    },
    // Deprecated
    PAID_AND_ORGANIC_IMPRESSIONS: {
      label: 'Impressions - Total (Discontinued)',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicImpressions',
      value: 'FACEBOOK_PAID_AND_ORGANIC_IMPRESSIONS',
    },
    ORGANIC_VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      attribute: 'organicViews',
      value: 'FACEBOOK_ORGANIC_VIEWS',
    },

    PAID_VIEWS: {
      label: 'Views',
      postType: postTypes.PROMOTED,
      attribute: 'paidViews',
      value: 'FACEBOOK_PAID_VIEWS',
    },
    PAID_AND_ORGANIC_VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicViews',
      value: 'FACEBOOK_PAID_AND_ORGANIC_VIEWS',
    },
    COMPETITIVE_ENGAGEMENT_RATE: {
      label: 'Engagement Rate - Public',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      attribute: 'engagementRatePublic',
      value: 'FACEBOOK_ENGAGEMENT_RATE_PUBLIC',
    },
    COMPETITIVE_ENGAGEMENTS: {
      label: 'Engagements - Public',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      attribute: 'totalEngagementsPublic',
      value: 'FACEBOOK_TOTAL_ENGAGEMENTS_PUBLIC',
    },

    // Deprecated
    ORGANIC_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      attribute: 'organicEngagements',
      value: 'FACEBOOK_ORGANIC_ENGAGEMENTS',
    },
    ORGANIC_ENGAGEMENTS_V2: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      attribute: 'organicEngagementsV2',
      value: 'FACEBOOK_ORGANIC_ENGAGEMENTS_V2',
    },
    PAID_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.PROMOTED,
      attribute: 'paidEngagements',
      value: 'FACEBOOK_PAID_ENGAGEMENTS',
    },
    // Deprecated
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      value: 'FACEBOOK_TOTAL_ENGAGEMENTS',
      boardsIndex: {
        attribute: 'fbTotalEngagements',
        info: {
          title: 'Engagements',
          content: 'The number of engagements your posts in this Board received',
          footer:
            'Engagements = Reactions + Comments + Shares + Post Clicks + Video View Clicks + Other Clicks',
        },
      },
      boardDetail: { attribute: 'total_engagements' },
      yourPosts: { attribute: 'totalEngagements' },
    },
    PAID_AND_ORGANIC_ENGAGEMENTS_V2: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicEngagementsV2',
      value: 'FACEBOOK_PAID_AND_ORGANIC_ENGAGEMENTS_V2',
    },
    // Deprecated
    LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_LINK_CLICKS',
      boardDetail: { attribute: 'link_clicks' },
      yourPosts: { attribute: 'linkClicks' },
    },
    // Incorrect: PostType should be ORGANIC_PROMOTED
    // use PAID_AND_ORGANIC_LINK_CLICKS
    // can remove depending on data avalible on new naming.
    ORGANIC_LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC,
      attribute: 'organicLinkClicks',
      value: 'FACEBOOK_ORGANIC_LINK_CLICKS',
    },
    PAID_AND_ORGANIC_LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicLinkClicks',
      value: 'FACEBOOK_PAID_AND_ORGANIC_LINK_CLICKS',
    },
    VIDEO_COMPLETE_VIEWS: {
      label: 'Video Complete Views',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_VIDEO_COMPLETE_VIEWS',
      boardDetail: { attribute: 'video_complete_views' },
      yourPosts: { attribute: 'videoCompleteViews' },
    },
    ORGANIC_VIDEO_COMPLETE_VIEWS: {
      label: 'Video Complete Views',
      postType: postTypes.ORGANIC,
      attribute: 'organicVideoCompleteViews',
      value: 'FACEBOOK_ORGANIC_VIDEO_COMPLETE_VIEWS',
    },
    PAID_AND_ORGANIC_VIDEO_COMPLETE_VIEWS: {
      label: 'Video Complete Views',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicVideoCompleteViews',
      value: 'FACEBOOK_PAID_AND_ORGANIC_VIDEO_COMPLETE_VIEWS',
    },
    // Incorrect: PostType should be ORGANIC_PROMOTED
    // use PAID_AND_ORGANIC_OTHER_CLICKS
    // can remove depending on data avalible on new naming.
    OTHER_CLICKS: {
      label: 'Other Clicks',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_OTHER_CLICKS',
      boardDetail: { attribute: 'other_clicks' },
      yourPosts: { attribute: 'otherClicks' },
    },
    PAID_AND_ORGANIC_OTHER_CLICKS: {
      label: 'Other Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicOtherClicks',
      value: 'FACEBOOK_PAID_AND_ORGANIC_OTHER_CLICKS',
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_VIDEO_VIEWS',
      boardDetail: { attribute: 'video_views' },
      yourPosts: { attribute: 'videoViews' },
    },
    ORGANIC_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC,
      attribute: 'organicVideoViews',
      value: 'FACEBOOK_ORGANIC_VIDEO_VIEWS',
    },
    PAID_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.PROMOTED,
      attribute: 'paidVideoViews',
      value: 'FACEBOOK_PAID_VIDEO_VIEWS',
    },
    PAID_AND_ORGANIC_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicVideoViews',
      value: 'FACEBOOK_PAID_AND_ORGANIC_VIDEO_VIEWS',
    },
    // Incorrect: PostType should be ORGANIC_PROMOTED
    // use PAID_AND_ORGANIC_PHOTO_VIEW_CLICKS
    // can remove depending on data avalible on new naming.
    PHOTO_VIEW_CLICKS: {
      label: 'Photo View Clicks',
      postType: postTypes.ORGANIC,
      value: 'FACEBOOK_PHOTO_VIEW_CLICKS',
      boardDetail: { attribute: 'photo_view_clicks' },
      yourPosts: { attribute: 'photoViewClicks' },
    },
    PAID_AND_ORGANIC_PHOTO_VIEW_CLICKS: {
      label: 'Photo View Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      attribute: 'paidAndOrganicPhotoViewClicks',
      value: 'FACEBOOK_PAID_AND_ORGANIC_PHOTO_VIEW_CLICKS',
    },
    CPC: {
      label: 'CPC',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'cpc', value: 'FACEBOOK_CPC' },
    },
    AMOUNT_SPEND: {
      label: 'Amount Spend',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'amountSpend', value: 'FACEBOOK_AMOUNT_SPEND' },
    },
    CPM: {
      label: 'CPM',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'cpm', value: 'FACEBOOK_CPM' },
    },
    CTR: {
      label: 'CTR',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'ctr', value: 'FACEBOOK_CTR' },
    },
    COST_PER_THRUPLAYS: {
      label: 'Cost per ThruPlay',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'costPerThruplays', value: 'FACEBOOK_COST_PER_THRUPLAYS' },
    },
    FREQUENCY: {
      label: 'Frequency',
      postType: postTypes.PROMOTED,
      yourPosts: {
        attribute: 'frequency',
        value: 'FACEBOOK_FREQUENCY',
      },
    },
    THRUPLAYS: {
      label: 'ThruPlays',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'thruplays', value: 'FACEBOOK_THRUPLAYS' },
    },
    VIDEO_PLAYS_25: {
      label: 'Video Plays 25%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays25', value: 'FACEBOOK_VIDEO_PLAYS_25' },
    },
    VIDEO_PLAYS_50: {
      label: 'Video Plays 50%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays50', value: 'FACEBOOK_VIDEO_PLAYS_50' },
    },
    VIDEO_PLAYS_75: {
      label: 'Video Plays 75%',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'videoPlays75', value: 'FACEBOOK_VIDEO_PLAYS_75' },
    },
    PAID_LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.PROMOTED,
      yourPosts: { attribute: 'paidLinkClicks', value: 'FACEBOOK_PAID_LINK_CLICKS' },
    },
    PAID_VIDEO_COMPLETE_VIEWS: {
      label: 'Video Complete Views',
      postType: postTypes.PROMOTED,
      yourPosts: {
        attribute: 'paidVideoCompleteViews',
        value: 'FACEBOOK_PAID_VIDEO_COMPLETE_VIEWS',
      },
    },
    // Deprecated and Incorrect: PostType should be ORGANIC_PROMOTED, has new label
    // use PAID_AND_ORGANIC_REELS_FB_REELS_TOTAL_PLAYS and remove after feature launch
    REELS_FB_REELS_TOTAL_PLAYS: {
      label: 'Reels Total Plays',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'reel.fbReelsTotalPlays',
        value: 'FACEBOOK_REELS_FB_REELS_TOTAL_PLAYS',
      },
    },
    // Deprecated
    PAID_AND_ORGANIC_REELS_FB_REELS_TOTAL_PLAYS: {
      label: 'Total Plays',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.fbReelsTotalPlays',
        value: 'FACEBOOK_REELS_FB_REELS_TOTAL_PLAYS',
      },
    },
    // Incorrect: PostType should be ORGANIC_PROMOTED, has new label
    // use PAID_AND_ORGANIC_REELS_POST_VIDEO_AVG_TIME_WATCHED and remove after feature launch
    REELS_POST_VIDEO_AVG_TIME_WATCHED: {
      label: 'Reels Avg. Time Watched',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'reel.postVideoAvgTimeWatched',
        value: 'FACEBOOK_REELS_POST_VIDEO_AVG_TIME_WATCHED',
      },
    },
    PAID_AND_ORGANIC_REELS_POST_VIDEO_AVG_TIME_WATCHED: {
      label: 'Avg. Time Watched',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoAvgTimeWatched',
        value: 'FACEBOOK_REELS_POST_VIDEO_AVG_TIME_WATCHED',
      },
    },
    // Deprecated and Incorrect:  PostType should be ORGANIC_PROMOTED
    // use PAID_AND_ORGANIC_REELS_EFFECTIVENESS and remove after feature launch
    REELS_EFFECTIVENESS: {
      label: 'Reels Effectiveness',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'reel.effectiveness',
        value: 'FACEBOOK_REELS_EFFECTIVENESS',
      },
    },
    // Deprecated
    PAID_AND_ORGANIC_REELS_EFFECTIVENESS: {
      label: 'Reels Effectiveness',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.effectiveness',
        value: 'FACEBOOK_REELS_EFFECTIVENESS',
      },
    },
    // Incorrect: PostType should be ORGANIC_PROMOTED, has new label
    // use PAID_AND_ORGANIC_BLUE_REELS_PLAYS_COUNT and remove after feature launch
    BLUE_REELS_PLAYS_COUNT: {
      label: 'Plays',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'reel.blueReelsPlayCount',
        value: 'FACEBOOK_BLUE_REELS_PLAYS_COUNT',
      },
    },
    PAID_AND_ORGANIC_BLUE_REELS_PLAYS_COUNT: {
      label: 'Plays',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.blueReelsPlayCount',
        value: 'FACEBOOK_BLUE_REELS_PLAYS_COUNT',
      },
    },
    // Deprecated and Incorrect: PostType should be ORGANIC_PROMOTED, has new label
    // use PAID_AND_ORGANIC_REELS_REPLAY_COUNT and remove after feature launch
    REELS_REPLAY_COUNT: {
      label: 'Reels Replays',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'reel.fbReelsReplayCount',
        value: 'FACEBOOK_REELS_REPLAY_COUNT',
      },
    },
    // Deprecated
    PAID_AND_ORGANIC_REELS_REPLAY_COUNT: {
      label: 'Replays',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.fbReelsReplayCount',
        value: 'FACEBOOK_REELS_REPLAY_COUNT',
      },
    },
    REELS_POST_IMPRESSIONS_UNIQUE: {
      label: 'Unique Impressions',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'reel.post_impressions_unique',
        value: 'FACEBOOK_REELS_POST_IMPRESSIONS_UNIQUE',
      },
      yourPosts: {
        attribute: 'reel.postImpressionsUnique',
        value: 'FACEBOOK_REELS_POST_IMPRESSIONS_UNIQUE',
      },
    },
    REELS_POST_VIDEO_FOLLOWERS: {
      label: 'Follows',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoFollowers',
        value: 'FACEBOOK_REELS_POST_VIDEO_FOLLOWERS',
      },
    },
    REELS_POST_VIDEO_VIEW_TIME: {
      label: 'Total Time Watched',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'reel.post_video_view_time',
        value: 'FACEBOOK_REELS_POST_VIDEO_VIEW_TIME',
      },
      yourPosts: {
        attribute: 'reel.postVideoViewTime',
        value: 'FACEBOOK_REELS_POST_VIDEO_VIEW_TIME',
      },
    },
    REELS_REACTION_ANGRY: {
      label: 'Reactions - Angry',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeAngry',
        value: 'FACEBOOK_REELS_REACTION_ANGRY',
      },
    },
    REELS_REACTION_HAHA: {
      label: 'Reactions - Haha',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeHaha',
        value: 'FACEBOOK_REELS_REACTION_HAHA',
      },
    },
    REELS_REACTION_LIKE: {
      label: 'Reactions - Like',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeLike',
        value: 'FACEBOOK_REELS_REACTION_LIKE',
      },
    },
    REELS_REACTION_LOVE: {
      label: 'Reactions - Love',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeLove',
        value: 'FACEBOOK_REELS_REACTION_LOVE',
      },
    },
    REELS_REACTION_SAD: {
      label: 'Reactions - Sad',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeSad',
        value: 'FACEBOOK_REELS_REACTION_SAD',
      },
    },
    REELS_REACTION_WOW: {
      label: 'Reactions - Wow',
      postType: postTypes.ORGANIC_PROMOTED,
      yourPosts: {
        attribute: 'reel.postVideoReactionTypeWow',
        value: 'FACEBOOK_REELS_REACTION_WOW',
      },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'fbAvgEngagementRate',
        value: 'FACEBOOK_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Reactions + Shares + Comments) / Views',
        },
      },
    },
    AVG_EFFECTIVENESS: {
      label: 'Avg. Effectiveness',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'fbAvgEffectiveness',
        value: 'FACEBOOK_AVG_EFFECTIVENESS',
        info: {
          title: 'Average Effectiveness',
          content: 'The average effectiveness of posts in this Board',
          footer: 'Effectiveness = (Engagements + Video Views)/ Reach',
        },
      },
    },
    AVG_REACH: {
      label: 'Avg. Reach',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'fbAvgReach',
        value: 'FACEBOOK_AVG_REACH',
        info: {
          title: 'Average Reach',
          content: 'The average reach for your posts in this Board',
          footer: 'Average Reach = Reach / Number of Media',
        },
      },
    },
    TOTAL_IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'fbTotalImpressions',
        value: 'FACEBOOK_TOTAL_IMPRESSIONS',
        info: {
          title: 'Impressions',
          content: 'The number of times your posts in this Board were displayed',
        },
      },
    },
    AVG_PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2: {
      label: 'Avg. Engagement Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      attribute: 'fbAvgPaidAndOrganicEngagementRateV2',
      info: {
        title: 'Average Engagement Rate',
        content: 'The average engagement rate for your posts',
        footer:
          'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Reactions + Shares + Comments) / Views',
      },
      boardsIndex: {
        value: 'FACEBOOK_AVG_PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2',
      },
      boardDetail: {
        label: 'Avg. Eng. Rate',
      },
    },
    AVG_PAID_AND_ORGANIC_EFFECTIVENESS_V2: {
      label: 'Avg. Effectiveness',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      attribute: 'fbAvgPaidAndOrganicEffectivenessV2',
      info: {
        title: 'Average Effectiveness',
        content: 'The percentage of users that saw your organic post and engaged with it',
        footer: 'Effectiveness = (Engagements / Reach) / Number of Posts',
      },
      boardsIndex: {
        value: 'FACEBOOK_AVG_PAID_AND_ORGANIC_EFFECTIVENESS_V2',
      },
    },
    AVG_PAID_AND_ORGANIC_REACH_V2: {
      label: 'Avg. Reach',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      attribute: 'fbAvgPaidAndOrganicReachV2',
      info: {
        title: 'Average Reach',
        content: 'The average reach for your posts',
        footer: 'Average Reach = Reach / Number of Posts',
      },
      boardsIndex: {
        value: 'FACEBOOK_AVG_PAID_AND_ORGANIC_REACH_V2',
      },
    },
    TOTAL_PAID_AND_ORGANIC_VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      attribute: 'fbTotalPaidAndOrganicViews',
      info: {
        title: 'Views',
        content:
          'The number of times your videos were played for at least 1ms plus the number of times your photos and text posts were on screen',
      },
      boardsIndex: {
        value: 'FACEBOOK_TOTAL_PAID_AND_ORGANIC_VIEWS',
      },
    },
    TOTAL_PAID_AND_ORGANIC_ENGAGEMENTS_V2: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      attribute: 'fbTotalPaidAndOrganicEngagementsV2',
      info: {
        title: 'Engagements',
        content: 'The sum of engagements on your posts',
        footer:
          'Engagements = Reactions + Shares + Comments\n\nReactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
      },
      boardsIndex: {
        value: 'FACEBOOK_TOTAL_PAID_AND_ORGANIC_ENGAGEMENTS_V2',
      },
    },
  },
  PINTEREST: {
    ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'engagements', value: 'PINTEREST_ENGAGEMENTS' },
      yourPosts: { attribute: 'engagements', value: 'PINTEREST_ENGAGEMENTS' },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'engagement', value: 'PINTEREST_ENGAGEMENT' },
      yourPosts: { attribute: 'engagementRate', value: 'PINTEREST_ENGAGEMENT_RATE' },
    },
    IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_impressions', value: 'PINTEREST_IMPRESSIONS' },
      yourPosts: { attribute: 'totalImpressions', value: 'PINTEREST_TOTAL_IMPRESSIONS' },
    },
    OUTBOUND_CLICKS: {
      label: 'Outbound Clicks',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_clicks', value: 'PINTEREST_CLICKS' },
      yourPosts: { attribute: 'totalClicks', value: 'PINTEREST_TOTAL_CLICKS' },
    },
    SAVES: {
      label: 'Saves',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_saves', value: 'PINTEREST_SAVES' },
      yourPosts: { attribute: 'totalSaves', value: 'PINTEREST_TOTAL_SAVES' },
    },
    PIN_CLICKS: {
      label: 'Pin Clicks',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_closeups', value: 'PINTEREST_CLOSEUPS' },
      yourPosts: { attribute: 'totalCloseups', value: 'PINTEREST_TOTAL_CLOSEUPS' },
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'total_video_views', value: 'PINTEREST_VIDEO_VIEWS' },
      yourPosts: { attribute: 'totalVideoViews', value: 'PINTEREST_TOTAL_VIDEO_VIEWS' },
    },
    AVERAGE_VIDEO_WATCH_TIME: {
      label: 'Avg. Watch Time',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'average_video_watch_time',
        value: 'PINTEREST_AVERAGE_VIDEO_WATCH_TIME',
      },
      yourPosts: {
        attribute: 'averageVideoWatchTime',
        value: 'PINTEREST_AVERAGE_VIDEO_WATCH_TIME',
      },
    },
    TOTAL_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'pinterestAvgEngagementRate',
        value: 'TOTAL_ENGAGEMENT',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your Pins in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Pins\n\nEngagement Rate = (Pin Clicks + Outbound Clicks + Saves) / Views',
        },
      },
    },
    TOTAL_CLICKS: {
      label: 'Outbound Clicks',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'pinterestTotalClicks',
        value: 'TOTAL_CLICKS',
        info: {
          title: 'Outbound Clicks',
          content:
            'The total number of times people clicked and followed the links attached to your Pins to the destination URL',
        },
      },
    },
    TOTAL_SAVES: {
      label: 'Saves',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'pinterestTotalSaves',
        value: 'TOTAL_SAVES',
        info: {
          title: 'Saves',
          content: 'The number of times your Pins in this Board were saved',
        },
      },
    },
    TOTAL_IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'pinterestTotalImpressions',
        value: 'TOTAL_IMPRESSIONS',
        info: {
          title: 'Impressions',
          content: 'The number of times your Pins in this Board were displayed',
        },
      },
    },
    TOTAL_PIN_CLICKS: {
      label: 'Pin Clicks',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'pinterestTotalCloseups',
        value: 'TOTAL_CLOSEUPS',
        info: {
          title: 'Pin Clicks',
          content: 'The number of times pins were clicked to see more details',
        },
      },
    },
  },
  YOUTUBE: {
    AVG_VIEW_PERCENTAGE: {
      label: 'Avg. % Viewed',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'avgViewPercentage', value: 'YOUTUBE_AVG_VIEW_PERCENTAGE' },
      yourPosts: { attribute: 'avgViewPercentage', value: 'YOUTUBE_AVG_VIEW_PERCENTAGE' },
    },
    AVG_VIEW_DURATION: {
      label: 'Avg. View Duration',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'duration',
      boardDetail: { attribute: 'avgViewDuration', value: 'YOUTUBE_AVG_VIEW_DURATION' },
      boardsIndex: {
        attribute: 'youtubeAvgViewDuration',
        value: 'YOUTUBE_AVG_VIEW_DURATION',
        info: {
          title: 'Average View Duration',
          content: 'The average amount of time viewers spent watching per video view',
        },
      },
      yourPosts: { attribute: 'avgViewDuration', value: 'YOUTUBE_AVG_VIEW_DURATION' },
    },
    CARD_CLICKS: {
      label: 'Card Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardClicks', value: 'YOUTUBE_CARD_CLICKS' },
      yourPosts: { attribute: 'cardClicks', value: 'YOUTUBE_CARD_CLICKS' },
    },
    CARD_CLICK_RATE: {
      label: 'Card Click Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardClickRate', value: 'YOUTUBE_CARD_CLICK_RATE' },
      yourPosts: { attribute: 'cardClickRate', value: 'YOUTUBE_CARD_CLICK_RATE' },
    },
    CARD_IMPRESSIONS: {
      label: 'Card Impressions',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardImpressions', value: 'YOUTUBE_CARD_IMPRESSIONS' },
      yourPosts: { attribute: 'cardImpressions', value: 'YOUTUBE_CARD_IMPRESSIONS' },
    },
    CARD_TEASER_CLICKS: {
      label: 'Card Teaser Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardTeaserClicks', value: 'YOUTUBE_CARD_TEASER_CLICKS' },
      yourPosts: { attribute: 'cardTeaserClicks', value: 'YOUTUBE_CARD_TEASER_CLICKS' },
    },
    CARD_TEASER_CLICK_RATE: {
      label: 'Card Teaser Click Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardTeaserClickRate', value: 'YOUTUBE_CARD_TEASER_CLICK_RATE' },
      yourPosts: { attribute: 'cardTeaserClickRate', value: 'YOUTUBE_CARD_TEASER_CLICK_RATE' },
    },
    CARD_TEASER_IMPRESSIONS: {
      label: 'Card Teaser Impressions',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'cardTeaserImpressions', value: 'YOUTUBE_CARD_TEASER_IMPRESSIONS' },
      yourPosts: { attribute: 'cardTeaserImpressions', value: 'YOUTUBE_CARD_TEASER_IMPRESSIONS' },
    },
    COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'comments', value: 'YOUTUBE_COMMENTS' },
      yourPosts: { attribute: 'comments', value: 'YOUTUBE_COMMENTS' },
    },
    DISLIKES: {
      label: 'Dislikes',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'dislikes', value: 'YOUTUBE_DISLIKES' },
      yourPosts: { attribute: 'dislikes', value: 'YOUTUBE_DISLIKES' },
    },
    ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'engagements', value: 'YOUTUBE_ENGAGEMENTS' },
      yourPosts: { attribute: 'engagements', value: 'YOUTUBE_ENGAGEMENTS' },
    },
    LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'likes', value: 'YOUTUBE_LIKES' },
      yourPosts: { attribute: 'likes', value: 'YOUTUBE_LIKES' },
    },
    PREMIUM_VIEWS: {
      label: 'Premium Views',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'premiumViews', value: 'YOUTUBE_PREMIUM_VIEWS' },
      yourPosts: { attribute: 'premiumViews', value: 'YOUTUBE_PREMIUM_VIEWS' },
    },
    PREMIUM_WATCH_TIME: {
      label: 'Premium Watch Time',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'estPremiumSecondsWatched',
        value: 'YOUTUBE_EST_PREMIUM_SECONDS_WATCHED',
      },
      yourPosts: {
        attribute: 'estPremiumSecondsWatched',
        value: 'YOUTUBE_EST_PREMIUM_SECONDS_WATCHED',
      },
    },
    SUBSCRIBERS: {
      label: 'Subscribers',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'subscribers', value: 'YOUTUBE_SUBSCRIBERS' },
      boardsIndex: {
        attribute: 'youtubeSubscribers',
        value: 'YOUTUBE_SUBSCRIBERS',
        info: {
          title: 'Subscribers',
          content: 'The net new number of subscribers to your channel from videos in this Board',
          footer: 'Net New Subscribers = Subscribers gained - subscribers lost',
        },
      },
      yourPosts: { attribute: 'subscribers', value: 'YOUTUBE_SUBSCRIBERS' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'shares', value: 'YOUTUBE_SHARES' },
      yourPosts: { attribute: 'shares', value: 'YOUTUBE_SHARES' },
    },
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'engagements', value: 'YOUTUBE_TOTAL_ENGAGEMENTS' },
      boardsIndex: {
        attribute: 'youtubeTotalEngagements',
        value: 'YOUTUBE_TOTAL_ENGAGEMENTS',
        info: {
          title: 'Engagements',
          content: 'The total number of engagements your videos in this Board received',
        },
      },
      yourPosts: { attribute: 'engagements', value: 'YOUTUBE_ENGAGEMENTS' },
    },
    VIDEOS_ADDED_TO_PLAYLIST: {
      label: 'Added to Playlist',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: {
        attribute: 'videosAddedToPlaylist',
        value: 'YOUTUBE_VIDEOS_ADDED_TO_PLAYLIST',
      },
      yourPosts: {
        attribute: 'videosAddedToPlaylist',
        value: 'YOUTUBE_VIDEOS_ADDED_TO_PLAYLIST',
      },
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'views', value: 'YOUTUBE_VIEWS' },
      yourPosts: { attribute: 'views', value: 'YOUTUBE_VIEWS' },
    },
    TOTAL_VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'youtubeTotalVideoViews',
        value: 'YOUTUBE_TOTAL_VIDEO_VIEWS',
        info: {
          title: 'Video Views',
          content: 'The number of times your videos in this Board were viewed',
        },
      },
    },
    WATCH_TIME: {
      label: 'Watch Time',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'estSecondsWatched', value: 'YOUTUBE_EST_SECONDS_WATCHED' },
      yourPosts: { attribute: 'estSecondsWatched', value: 'YOUTUBE_EST_SECONDS_WATCHED' },
    },
    TOTAL_WATCH_TIME: {
      label: 'Watch Time',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'duration',
      boardsIndex: {
        attribute: 'youtubeTotalWatchTime',
        value: 'YOUTUBE_TOTAL_WATCH_TIME',
        info: {
          title: 'Watch Time',
          content: 'The total amount of time that users watched your videos in this Board',
        },
      },
    },
  },
  TWITTER: {
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'engagementRate', value: 'TWITTER_ENGAGEMENT_RATE' },
      yourPosts: { attribute: 'engagementRate', value: 'TWITTER_ENGAGEMENT_RATE' },
    },
    ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'engagements', value: 'TWITTER_TOTAL_ENGAGEMENTS' },
      boardsIndex: {
        attribute: 'twitterTotalEngagements',
        value: 'TWITTER_TOTAL_ENGAGEMENTS',
        info: {
          title: 'Engagements',
          content: 'The number of engagements your posts in this Board received',
          footer: 'Engagements = Reposts + Replies + Likes + Link Clicks',
        },
      },
      yourPosts: {
        attribute: 'engagements',
        value: 'TWITTER_TOTAL_ENGAGEMENTS',
      },
    },
    IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'impressions', value: 'TWITTER_IMPRESSIONS' },
      boardsIndex: {
        attribute: 'twitterImpressions',
        value: 'TWITTER_IMPRESSIONS',
        info: {
          title: 'Impressions',
          content: 'The number of times your posts in this Board were displayed',
        },
      },
      yourPosts: { attribute: 'impressions', value: 'TWITTER_IMPRESSIONS' },
    },
    LINK_CLICKS: {
      label: 'Link Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'urlClicks', value: 'TWITTER_URL_CLICKS' },
      yourPosts: { attribute: 'urlClicks', value: 'TWITTER_URL_CLICKS' },
    },
    PROFILE_CLICKS: {
      label: 'Profile Clicks',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'userProfileClicks', value: 'TWITTER_USER_PROFILE_CLICKS' },
      yourPosts: { attribute: 'userProfileClicks', value: 'TWITTER_USER_PROFILE_CLICKS' },
    },
    QUOTE_POSTS: {
      label: 'Quote Posts',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'quoteTweets', value: 'TWITTER_QUOTE_TWEETS' },
      yourPosts: { attribute: 'quoteTweets', value: 'TWITTER_QUOTE_TWEETS' },
    },
    REPLIES: {
      label: 'Replies',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'replies', value: 'TWITTER_REPLIES' },
      yourPosts: { attribute: 'replies', value: 'TWITTER_REPLIES' },
    },
    REPOSTS: {
      label: 'Reposts',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'retweets', value: 'TWITTER_RETWEETS' },
      boardsIndex: {
        attribute: 'twitterRetweets',
        value: 'TWITTER_RETWEETS',
        info: {
          title: 'Reposts',
          content: 'The number of times your posts in this Board were reposted',
        },
      },
      yourPosts: { attribute: 'retweets', value: 'TWITTER_RETWEETS' },
    },
    TOTAL_REPOSTS: {
      label: 'Total Reposts',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'totalRetweets', value: 'TWITTER_TOTAL_RETWEETS' },
      yourPosts: { attribute: 'totalRetweets', value: 'TWITTER_TOTAL_RETWEETS' },
    },
    USER_FOLLOWS: {
      label: 'User Follows',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'follows', value: 'TWITTER_FOLLOWS' },
      yourPosts: { attribute: 'follows', value: 'TWITTER_FOLLOWS' },
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC_PROMOTED,
      boardDetail: { attribute: 'videoViews', value: 'TWITTER_VIDEO_VIEWS' },
      yourPosts: { attribute: 'videoViews', value: 'TWITTER_VIDEO_VIEWS' },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'twitterAvgEngagementRate',
        value: 'TWITTER_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Likes + Replies + Reposts + Quotes) / Views',
        },
      },
      yourPosts: { attribute: 'engagementRate', value: 'TWITTER_ENGAGEMENT_RATE' },
    },
    LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC_PROMOTED,
      formatType: 'abbr',
      boardDetail: { attribute: 'likes', value: 'TWITTER_LIKES' },
      boardsIndex: {
        attribute: 'twitterLikes',
        value: 'TWITTER_LIKES',
        info: {
          title: 'Likes',
          content: 'The number of times your posts in this Board were liked',
        },
      },
      yourPosts: { attribute: 'likes', value: 'TWITTER_LIKES' },
    },
  },
  THREADS: {
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'threadsAvgEngagementRate',
        value: 'THREADS_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Likes + Replies + Reposts + Quotes) / Views',
        },
      },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      boardDetail: { attribute: 'engagementRate', value: 'THREADS_ENGAGEMENT_RATE' },
      yourPosts: { attribute: 'engagementRate', value: 'THREADS_ENGAGEMENT_RATE' },
    },
    ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'engagements', value: 'THREADS_ENGAGEMENTS' },
      yourPosts: { attribute: 'engagements', value: 'THREADS_ENGAGEMENTS' },
    },
    LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'likes', value: 'THREADS_LIKES' },
      yourPosts: { attribute: 'likes', value: 'THREADS_LIKES' },
    },
    QUOTES: {
      label: 'Quotes',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'quotes', value: 'THREADS_QUOTES' },
      yourPosts: { attribute: 'quotes', value: 'THREADS_QUOTES' },
    },
    REPLIES: {
      label: 'Replies',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'replies', value: 'THREADS_REPLIES' },
      yourPosts: { attribute: 'replies', value: 'THREADS_REPLIES' },
    },
    REPOSTS: {
      label: 'Reposts',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'reposts', value: 'THREADS_REPOSTS' },
      yourPosts: { attribute: 'reposts', value: 'THREADS_REPOSTS' },
    },
    REPOSTS_AND_QUOTES: {
      label: 'Reposts',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'repostsAndQuotes', value: 'THREADS_REPOSTS_AND_QUOTES' },
      yourPosts: { attribute: 'repostsAndQuotes', value: 'THREADS_REPOSTS_AND_QUOTES' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'shares', value: 'THREADS_SHARES' },
      yourPosts: { attribute: 'shares', value: 'THREADS_SHARES' },
    },
    TOTAL_VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'threadsTotalViews',
        value: 'THREADS_TOTAL_VIEWS',
        info: {
          title: 'Views',
          content: 'The sum of views your posts in this Board received',
        },
      },
    },
    TOTAL_LIKES: {
      label: 'Likes',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'threadsTotalLikes',
        value: 'THREADS_TOTAL_LIKES',
        info: {
          title: 'Likes',
          content: 'The sum of likes your posts in this Board received',
        },
      },
    },
    TOTAL_REPLIES: {
      label: 'Replies',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'threadsTotalReplies',
        value: 'THREADS_TOTAL_REPLIES',
        info: {
          title: 'Replies',
          content: 'The sum of replies your posts in this Board received',
        },
      },
    },
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'threadsTotalEngagements',
        value: 'THREADS_TOTAL_ENGAGEMENTS',
        info: {
          title: 'Engagements',
          content: 'The sum of engagements your posts in this Board received',
          footer: 'Engagements = Likes + Replies + Reposts + Quotes',
        },
      },
    },
    VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardDetail: { attribute: 'views', value: 'THREADS_VIEWS' },
      yourPosts: { attribute: 'views', value: 'THREADS_VIEWS' },
    },
  },
  LINKEDIN: {
    AVG_CLICK_THROUGH_RATE: {
      label: 'Avg. Click Through Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'linkedinAvgClickThroughRate',
        value: 'LINKEDIN_AVG_CLICK_THROUGH_RATE',
        info: {
          title: 'Average Click Through Rate',
          content: 'The average click through rate for posts in this board',
          footer: 'Average Click Through Rate = (Clicks / Impressions) / Number of Posts',
        },
      },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Engagement Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'linkedinAvgEngagementRate',
        value: 'LINKEDIN_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for posts in this board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Clicks + Reactions + Comments + Reposts) / Views',
        },
      },
    },
    AVG_UNIQUE_IMPRESSIONS: {
      label: 'Avg. Unique Impressions',
      postType: postTypes.ORGANIC,
      formatType: 'abbr',
      boardsIndex: {
        attribute: 'linkedinAvgUniqueImpressions',
        value: 'LINKEDIN_AVG_UNIQUE_IMPRESSIONS',
        info: {
          title: 'Average Unique Impressions',
          content:
            'The average number times posts in this board were shown to unique LinkedIn members',
          footer: 'Average Unique Impressions = Unique Impressions / Number of Posts',
        },
      },
    },
    CLICK_THROUGH_RATE: {
      label: 'Click Through Rate',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'clickThroughRate', value: 'LINKEDIN_CLICK_THROUGH_RATE' },
      yourPosts: { attribute: 'clickThroughRate', value: 'LINKEDIN_CLICK_THROUGH_RATE' },
    },
    CLICKS: {
      label: 'Clicks',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'clicks', value: 'LINKEDIN_CLICKS' },
      yourPosts: { attribute: 'clicks', value: 'LINKEDIN_CLICKS' },
    },
    COMMENTS: {
      label: 'Comments',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'comments', value: 'LINKEDIN_COMMENTS' },
      yourPosts: { attribute: 'comments', value: 'LINKEDIN_COMMENTS' },
    },
    ENGAGEMENTS: {
      label: 'Engagements',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'engagements', value: 'LINKEDIN_ENGAGEMENTS' },
      yourPosts: { attribute: 'engagements', value: 'LINKEDIN_ENGAGEMENTS' },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'engagementRate', value: 'LINKEDIN_ENGAGEMENT_RATE' },
      yourPosts: { attribute: 'engagementRate', value: 'LINKEDIN_ENGAGEMENT_RATE' },
    },
    IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'impressions', value: 'LINKEDIN_IMPRESSIONS' },
      yourPosts: { attribute: 'impressions', value: 'LINKEDIN_IMPRESSIONS' },
    },
    LIKES: {
      label: 'Reactions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'likes', value: 'LINKEDIN_LIKES' },
      yourPosts: { attribute: 'likes', value: 'LINKEDIN_LIKES' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'shares', value: 'LINKEDIN_SHARES' },
      yourPosts: { attribute: 'shares', value: 'LINKEDIN_SHARES' },
    },
    TOTAL_IMPRESSIONS: {
      label: 'Impressions',
      postType: postTypes.ORGANIC,
      boardsIndex: {
        attribute: 'linkedinTotalImpressions',
        value: 'LINKEDIN_TOTAL_IMPRESSIONS',
        info: {
          title: 'Impressions',
          content: 'The number of times your posts in this Board were viewed',
        },
      },
    },
    TOTAL_LIKES: {
      label: 'Reactions',
      postType: postTypes.ORGANIC,
      boardsIndex: {
        attribute: 'linkedinTotalLikes',
        value: 'LINKEDIN_TOTAL_LIKES',
        info: {
          title: 'Reactions',
          content: 'The number of reactions to your posts in this Board',
          footer: 'Includes all reactions',
        },
      },
    },
    UNIQUE_IMPRESSIONS: {
      label: 'Unique Impressions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'uniqueImpressions', value: 'LINKEDIN_UNIQUE_IMPRESSIONS' },
      yourPosts: { attribute: 'uniqueImpressions', value: 'LINKEDIN_UNIQUE_IMPRESSIONS' },
    },
    VIDEO_VIEWS: {
      label: 'Video Views',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'videoAnalytics.videoViews',
        value: 'LINKEDIN_VIDEO_ANALYTICS_VIDEO_VIEWS',
      },
      yourPosts: {
        attribute: 'videoAnalyticsVideoViews',
        value: 'LINKEDIN_VIDEO_ANALYTICS_VIDEO_VIEWS',
      },
    },
    WATCH_TIME: {
      label: 'Watch Time',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'videoAnalytics.timeWatchedForViews',
        value: 'LINKEDIN_VIDEO_ANALYTICS_TIME_WATCHED_FOR_VIEWS',
      },
      yourPosts: {
        attribute: 'videoAnalyticsTimeWatchedForViews',
        value: 'LINKEDIN_VIDEO_ANALYTICS_TIME_WATCHED_FOR_VIEWS',
      },
    },
  },
  SNAPCHAT: {
    AVG_VIEW_TIME: {
      label: 'Avg. View Time',
      formatType: 'duration',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'avgViewTimeSeconds', value: 'SNAPCHAT_AVG_VIEW_TIME_SECONDS' },
      boardsIndex: {
        attribute: 'snapchatAvgViewTime',
        value: 'SNAPCHAT_AVG_VIEW_TIME',
        info: {
          title: 'Average View Time',
          content: 'The average view time for your posts in this Board',
        },
      },
      boardDetail: { attribute: 'avgViewTimeSeconds', value: 'SNAPCHAT_AVG_VIEW_TIME_SECONDS' },
    },
    ENGAGEMENTS: {
      label: 'Engagements',
      formatType: 'abbr',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'engagements', value: 'SNAPCHAT_ENGAGEMENTS' },
      boardDetail: { attribute: 'engagements', value: 'SNAPCHAT_ENGAGEMENTS' },
    },
    TOTAL_ENGAGEMENTS: {
      label: 'Engagements',
      formatType: 'abbr',
      postType: postTypes.ORGANIC,
      boardsIndex: {
        attribute: 'snapchatTotalEngagements',
        value: 'SNAPCHAT_TOTAL_ENGAGEMENTS',
        info: {
          title: 'Engagements',
          content: 'The sum of engagements your posts in this Board received',
          footer: 'Engagements = Favorites + Shares + Screenshots + Replies',
        },
      },
    },
    FAVORITES: {
      label: 'Favorites',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'favorites', value: 'SNAPCHAT_FAVORITES' },
      boardDetail: {
        attribute: 'favorites',
        value: 'SNAPCHAT_FAVORITES',
        info: { content: 'Includes Stories, Saved Stories and Spotlights' },
      },
    },
    SCREENSHOTS: {
      label: 'Screenshots',
      postType: postTypes.ORGANIC,
      yourPosts: {
        attribute: 'screenshots',
        value: 'SNAPCHAT_SCREENSHOTS',
        forMediaTypes: ['SPOTLIGHT'],
      },
      boardDetail: { attribute: 'screenshots', value: 'SNAPCHAT_SCREENSHOTS' },
    },
    SHARES: {
      label: 'Shares',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'shares', value: 'SNAPCHAT_SHARES' },
      boardDetail: { attribute: 'shares', value: 'SNAPCHAT_SHARES' },
    },
    VIEW_TIME: {
      label: 'View Time',
      formatType: 'duration',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'viewTimeSeconds', value: 'SNAPCHAT_VIEW_TIME_SECONDS' },
      boardDetail: { attribute: 'viewTimeSeconds', value: 'SNAPCHAT_VIEW_TIME_SECONDS' },
    },
    VIEWERS: {
      label: 'Viewers',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'viewers', value: 'SNAPCHAT_VIEWERS' },
      boardDetail: { attribute: 'viewers', value: 'SNAPCHAT_VIEWERS' },
    },
    TOTAL_VIEWERS: {
      label: 'Viewers',
      formatType: 'abbr',
      postType: postTypes.ORGANIC,
      boardsIndex: {
        attribute: 'snapchatTotalViewers',
        value: 'SNAPCHAT_TOTAL_VIEWERS',
        info: {
          title: 'Viewers',
          content: 'The sum of viewers your posts in this Board received',
        },
      },
    },
    VIEWS: {
      label: 'Views',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'views', value: 'SNAPCHAT_VIEWS' },
      boardDetail: { attribute: 'views', value: 'SNAPCHAT_VIEWS' },
    },
    TOTAL_VIEWS: {
      label: 'Views',
      formatType: 'abbr',
      postType: postTypes.ORGANIC,
      boardsIndex: {
        attribute: 'snapchatTotalViews',
        value: 'SNAPCHAT_TOTAL_VIEWS',
        info: {
          title: 'Views',
          content: 'The sum of views your posts in this Board received',
        },
      },
    },
    COMPLETES: {
      label: 'Completes',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'completes',
        value: 'SNAPCHAT_COMPLETES',
        info: { content: 'Includes Stories and Saved Stories' },
      },
    },
    ENGAGEMENT_RATE: {
      label: 'Engagement Rate',
      postType: postTypes.ORGANIC,
      yourPosts: { attribute: 'engagementRate', value: 'SNAPCHAT_ENGAGEMENT_RATE' },
      boardDetail: { attribute: 'engagementRate', value: 'SNAPCHAT_ENGAGEMENT_RATE' },
    },
    INTERACTIONS: {
      label: 'Interactions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'interactions', value: 'SNAPCHAT_INTERACTIONS' },
    },
    REPLIES: {
      label: 'Replies',
      postType: postTypes.ORGANIC,
      boardDetail: {
        attribute: 'replies',
        value: 'SNAPCHAT_REPLIES',
        info: { content: 'Includes Stories, Snaps, and Spotlights' },
      },
    },
    SWIPE_DOWNS: {
      label: 'Swipe Downs',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'swipeDowns', value: 'SNAPCHAT_SWIPE_DOWNS' },
    },
    SWIPE_UPS: {
      label: 'Swipe Ups',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'swipeUps', value: 'SNAPCHAT_SWIPE_UPS' },
    },
    UNIQUE_SESSIONS: {
      label: 'Unique Sessions',
      postType: postTypes.ORGANIC,
      boardDetail: { attribute: 'uniqueSessions', value: 'SNAPCHAT_UNIQUE_SESSIONS' },
    },
    AVG_ENGAGEMENT_RATE: {
      label: 'Avg. Eng. Rate',
      postType: postTypes.ORGANIC,
      formatType: 'percent',
      formatOptions: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      },
      boardsIndex: {
        attribute: 'snapchatAvgEngagementRate',
        value: 'SNAPCHAT_AVG_ENGAGEMENT_RATE',
        info: {
          title: 'Average Engagement Rate',
          content: 'The average engagement rate for your posts in this Board',
          footer:
            'Avg. Engagement Rate = Sum of Engagement Rates / Number of Posts\n\nEngagement Rate = (Favorites + Shares + Screenshots + Replies) / Views',
        },
      },
    },
  },
  LIBRARY: {
    CLICKS: {
      label: 'Clicks',
      value: 'CLICKS',
      attribute: 'clicks',
    },
  },
});
